import React from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
    text: string;
    example?: string;
}

const WIDTH = 256;   // ancho deseado del globo
const MARGIN = 8;    // aire mínimo contra los bordes de la pantalla

/**
 * Tooltip de ayuda. Se abre al pasar el cursor (escritorio) o al tocar el
 * ícono (móvil), y se cierra al tocar fuera.
 *
 * El globo se renderiza en un portal sobre el body con posición fija y
 * calculada desde el ícono: así nunca se sale de la pantalla ni queda
 * recortado por contenedores con overflow.
 */
const Tooltip: React.FC<TooltipProps> = ({ text, example }) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const [box, setBox] = React.useState<{ left: number; bottom: number; width: number; arrow: number } | null>(null);

    const place = React.useCallback(() => {
        const a = anchorRef.current?.getBoundingClientRect();
        if (!a) return;
        const vw = document.documentElement.clientWidth;
        const width = Math.min(WIDTH, vw - MARGIN * 2);
        const centered = a.left + a.width / 2 - width / 2;
        // Encajar dentro de la pantalla
        const left = Math.max(MARGIN, Math.min(centered, vw - width - MARGIN));
        setBox({
            left,
            bottom: window.innerHeight - a.top + MARGIN,
            width,
            arrow: a.left + a.width / 2 - left, // la flecha sigue apuntando al ícono
        });
    }, []);

    React.useLayoutEffect(() => {
        if (!isVisible) { setBox(null); return; }
        place();
    }, [isVisible, place]);

    React.useEffect(() => {
        if (!isVisible) return;
        const close = () => setIsVisible(false);
        document.addEventListener('click', close);
        window.addEventListener('resize', place);
        window.addEventListener('scroll', close, true);
        return () => {
            document.removeEventListener('click', close);
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', close, true);
        };
    }, [isVisible, place]);

    return (
        <span className="relative inline-flex ml-1 align-middle">
            <button
                ref={anchorRef}
                type="button"
                aria-label="Más información"
                className="material-symbols-outlined text-[16px] text-slate-400 cursor-help hover:text-primary transition-colors bg-transparent border-0 p-0 leading-none"
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
                onClick={e => { e.stopPropagation(); setIsVisible(v => !v); }}
            >
                help
            </button>

            {isVisible && box && createPortal(
                <div
                    role="tooltip"
                    style={{ position: 'fixed', left: box.left, bottom: box.bottom, width: box.width }}
                    className="p-3 bg-slate-900 text-white text-xs rounded-xl shadow-xl z-[100]"
                >
                    <p className="leading-relaxed">{text}</p>
                    {example && (
                        <p className="mt-2 pt-2 border-t border-slate-700 text-slate-300">
                            <span className="font-semibold text-white">Ejemplo:</span> {example}
                        </p>
                    )}
                    <span
                        style={{ left: box.arrow }}
                        className="absolute top-full -translate-x-1/2 border-4 border-transparent border-t-slate-900"
                    />
                </div>,
                document.body
            )}
        </span>
    );
};

export default Tooltip;
